import { useEffect } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { identifyUser, resetUser } from "@/lib/featureFlags";

export type CurrentUser = {
  id: string;
  clerkId: string;
  email: string | null;
  name: string | null;
  image: string | null;
  role: "superadmin" | "schooladmin" | "marketer" | "user";
  clientSlug: string;
  isSuspended: boolean;
  athleteId: string | null;
  ambassadorId: string | null;
  phone: string | null;
  promoCodeUsed?: {
    id: string;
    code: string;
  } | null;
};

export function useCurrentUser() {
  const { isSignedIn, isLoaded, getToken } = useAuth();
  const canFetchUser = isLoaded && isSignedIn;

  const query = useQuery({
    queryKey: ["auth", "me"],
    enabled: canFetchUser,
    retry: false,
    queryFn: async () => {
      const token = await getToken();
      if (!token) {
        throw new Error("Missing auth token");
      }
      return api.get<CurrentUser>("/api/auth/me", token);
    },
  });

  useEffect(() => {
    if (!canFetchUser) {
      resetUser();
      return;
    }

    if (query.error) {
      console.error("[useCurrentUser] Failed to fetch user:", query.error);
      resetUser();
      return;
    }

    if (query.data) {
      identifyUser(query.data.id, {
        email: query.data.email,
        role: query.data.role,
        clientSlug: query.data.clientSlug,
      });
    }
  }, [canFetchUser, query.data, query.error]);

  const user = canFetchUser ? (query.data ?? null) : null;
  const isLoading = canFetchUser ? query.isLoading : false;

  return { user, isLoading, isAdmin: user?.role === "superadmin" };
}
